import {
  activateIntegration,
  connectIntegrationDevice,
  deactivateIntegration,
  getIntegrationDevices,
  getIntegrationStatus,
} from '@/api';
import { IntegrationStatus } from '@/pages/Integration/constants';
import { IntegrationDevice } from '@/pages/ProfilePage/components/IntegrationsTab/IntegrationsTab';
import logger from '@/utils/logger';
import showToast from '@jpi-cloud-web-pro/jpi-cloud-shared/components/Notification';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';

import { Brands } from '@jpi-cloud-web-pro/jpi-cloud-shared/config/brands';
import { useIntl } from 'react-intl';
import useServicePartner from './useServicePartner';
import useUserInfo from './useUserInfo';

export const useIntegration = () => {
  const [loading, setLoading] = useState(false);
  const [integrationState, setIntegrationState] = useState(null);
  const [integrationDevicesList, setIntegrationDevicesList] = useState([]);

  const intl = useIntl();
  const { spId } = useUserInfo();

  const fetchIntegrationStateQuery = (spId: number) =>
    useQuery({
      queryKey: ['integration-state', spId],
      queryFn: async () => {
        const response = await getIntegrationStatus(spId);
        return response?.data[0] || null;
      },
      enabled: !!spId,
    });

  const {
    data: integrationStateData,
    isLoading: isLoadingIntegrationState,
    isError,
  } = fetchIntegrationStateQuery(spId!);

  useEffect(() => {
    if (integrationStateData) {
      setIntegrationState({ ...integrationStateData });
    }

    if (isError) {
      setIntegrationState(null);
    }
  }, [integrationStateData, isError]);

  const activateMutation = useMutation({
    mutationFn: async () => {
      await activateIntegration(spId, 'Climatix');
    },
    onMutate: () => {
      setLoading(true);
    },
    onSuccess: () => {
      setIntegrationState({ ...integrationState, status: IntegrationStatus.ACTIVE });
      showToast(
        'success',
        intl.formatMessage({
          id: 'integration.notification.activate.success',
          defaultMessage: 'NIBE Flow integration has been activated.',
        }),
      );
    },
    onError: (error: AxiosError) => {
      setIntegrationState({ ...integrationState, status: IntegrationStatus.INACTIVE });
      showToast(
        'error',
        intl.formatMessage({
          id: 'integration.notification.activate.error',
          defaultMessage: 'We couldn’t activate the integration. Please try again.',
        }),
      );
      logger.toAppInsights(error);
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: async () => {
      await deactivateIntegration(spId, 'Climatix');
    },
    onMutate: () => {
      setLoading(true);
    },
    onSuccess: () => {
      setIntegrationState({ ...integrationState, status: IntegrationStatus.INACTIVE });
      showToast(
        'success',
        intl.formatMessage({
          id: 'integration.notification.deactivate.success',
          defaultMessage: 'NIBE Flow integration has been successfully deactivated.',
        }),
      );
    },
    onError: (error: AxiosError) => {
      setIntegrationState({ ...integrationState, status: IntegrationStatus.ACTIVE });
      showToast(
        'error',
        intl.formatMessage({
          id: 'integration.notification.deactivate.error',
          defaultMessage: 'We couldn’t deactivate the integration. Please try again.',
        }),
      );
      logger.toAppInsights(error);
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  const getDevicesMutation = useMutation({
    mutationFn: async () => {
      if (spId && integrationState?.status === IntegrationStatus.ACTIVE) {
        const response = await getIntegrationDevices(spId, 'Climatix');
        return response?.data || [];
      }
      return [];
    },
    onMutate: () => {
      setLoading(true);
    },
    onSuccess: data => {
      setIntegrationDevicesList(data);
    },
    onError: (error: AxiosError) => {
      showToast(
        'error',
        intl.formatMessage({
          id: 'integration.notification.fetch.error',
          defaultMessage: 'We couldn’t get the NIBE Flow devices. Please try again.',
        }),
      );
      logger.toAppInsights(error);
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  const connectDevicesMutation = useMutation({
    mutationFn: async (devices: IntegrationDevice[]) => {
      if (devices.length === 0) return;
      if (spId) {
        const connectDeviceRequests = devices.map(device =>
          connectIntegrationDevice(spId, 'Climatix', device.externalId),
        );
        const results = await Promise.allSettled(connectDeviceRequests);
        return { results, devices };
      }
      return { results: [], devices };
    },
    onMutate: () => {
      setLoading(true);
    },
    onSuccess: ({ results, devices }) => {
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          logger.toAppInsights(new Error(`Failed to connect device with ID: ${devices[index].name}`));
          if ((result.reason as AxiosError)?.response?.status == 422) {
            showToast(
              'error',
              `${intl.formatMessage({
                id: 'integration.notification.connect.error.422.message',
                defaultMessage:
                  'Device registration failed. The address is incomplete or contains an invalid country. Please check your address and country, then try again.',
              })}`,
            );
          } else {
            showToast(
              'error',
              `${intl.formatMessage({
                id: 'integration.notification.connect.error.prefix',
                defaultMessage: 'Failed to fetch',
              })} ${devices[index].name}. ${intl.formatMessage({
                id: 'integration.notification.connect.error.postfix',
                defaultMessage: 'Please try again.',
              })}`,
            );
          }
        }
        if (result.status === 'fulfilled') {
          showToast(
            'success',
            `${devices[index].name} ${intl.formatMessage({
              id: 'integration.notification.connect.success',
              defaultMessage: 'is fetched successfully',
            })}`,
          );
        }
      });
    },
    onError: (error: AxiosError) => {
      showToast(
        'error',
        intl.formatMessage({
          id: 'integration.notification.connect.error.general',
          defaultMessage: 'We couldn’t connect the devices. Please try again.',
        }),
      );
      logger.toAppInsights(error);
    },
    onSettled: () => {
      setLoading(false);
      runGetIntegrationDevices();
    },
  });

  const runActivateIntegration = () => {
    if (spId) {
      activateMutation.mutate();
    }
  };

  const runDeactivateIntegration = () => {
    if (spId) {
      deactivateMutation.mutate();
    }
  };

  const runGetIntegrationDevices = () => {
    getDevicesMutation.mutate();
  };

  const connectIntegrationDevices = (devices: IntegrationDevice[]) => {
    connectDevicesMutation.mutate(devices);
  };

  return {
    loading: loading || isLoadingIntegrationState,
    integrationState,
    integrationDevicesList,
    runActivateIntegration,
    runDeactivateIntegration,
    runGetIntegrationDevices,
    connectIntegrationDevices,
  };
};

/**
 * Note: This is a lightweight integration presence hook.
 * Returns a Set of service partner brand IDs and a boolean flag
 * indicating whether NIBE Flow integration (NIBE/NIBEF) is present.
 */
export const useIntegrationPresence = () => {
  const servicePartner = useServicePartner();

  const brandIdSet = useMemo(() => {
    const ids = servicePartner?.brands?.map(b => b?.brandId?.toLowerCase()) ?? [];
    return new Set(ids);
  }, [servicePartner?.brands]);

  const showNibeFlowIntegration = useMemo(() => {
    return brandIdSet.has(Brands.NIBE) || brandIdSet.has(Brands.NIBEF);
  }, [brandIdSet]);

  return { brandIdSet, showNibeFlowIntegration };
};
